import React, { useState } from 'react';
import cn from 'classnames';
// Helpers
import {
  getDateLabelFromDeliverable,
  isContentOnlyDeliverable,
} from 'helpers/deliverables';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_task_CampaignContentTask,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CampaignType,
  CBDeliverableItemType,
  CbDeliverableV2Status,
} from 'api/graphql-global-types';
// Components
import RevisionHistoryModal from 'pages/CampaignDetails/CampaignDetails/components/TalentTable/components/CampaignDeliverablesReview/CampaignDeliverablesSubmissionReview/components/TimelineDeliverablesReviewForm/components/RevisionHistoryModal/RevisionHistoryModal';
// Styles
import styles from './DeliverableContentCard.module.scss';

type DeliverableContentCardProps = {
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  children?: React.ReactNode;
  className?: string;
  campaignType?: CampaignType;
};

const DeliverableContentCard: React.FC<DeliverableContentCardProps> = ({
  deliverable,
  children,
  className,
  campaignType,
}) => {
  const [isRevisionModalOpen, setIsRevisionModalOpen] =
    useState<boolean>(false);

  const { title, description, dueDate } =
    deliverable?.task as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_task_CampaignContentTask;

  const contentPiecesLength = deliverable?.items.filter(
    (item) => item.type === CBDeliverableItemType.FILE
  ).length;

  // Check if deliverable has been rejected and needs action
  const hasFeedback =
    deliverable?.status &&
    [
      CbDeliverableV2Status.CONTENT_REJECTED,
      CbDeliverableV2Status.TALENT_ACTION_NEEDED,
    ].includes(deliverable.status);

  const isOnlyContentDeliverablesType = isContentOnlyDeliverable(
    campaignType,
    deliverable
  );

  const handleRevisionHistoryToggle = () => {
    setIsRevisionModalOpen((prev) => !prev);
  };

  const getDeliverableLabel = () => {
    if (isOnlyContentDeliverablesType) {
      return `Content ${(deliverable?.task?.orderIdx ?? 0) + 1}`;
    }

    return `Deliverable ${
      (deliverable?.task?.orderIdx ?? 0) + 1
    }: ${title} - Content`;
  };

  return (
    <>
      <div className={cn(styles.root, className)}>
        <div className={styles.header}>
          <h3 className={styles.title}>{getDeliverableLabel()}</h3>
          <div className={styles.status}>{deliverable?.status}</div>
        </div>

        {description && <div className={styles.description}>{description}</div>}
        {!!contentPiecesLength && (
          <div className={styles.description}>
            Content pieces: {contentPiecesLength}
          </div>
        )}

        {dueDate && (
          <div className={styles.dueDate}>
            Due: {getDateLabelFromDeliverable('content', deliverable)}
          </div>
        )}

        {hasFeedback && (
          <div className={styles.feedbackSection}>
            <div className={styles.feedbackMessage}>
              <strong>Brand&apos;s Feedback</strong>
              <p>Review changes and feedback and upload deliverables again.</p>
            </div>
            <button
              className={styles.revisionButton}
              onClick={handleRevisionHistoryToggle}
            >
              See Feedback
            </button>
          </div>
        )}

        {children}
      </div>

      <RevisionHistoryModal
        deliverable={deliverable}
        isOpen={isRevisionModalOpen}
        onClose={handleRevisionHistoryToggle}
      />
    </>
  );
};

export default DeliverableContentCard;
